/**
 * "↻ RANDOMIZE COLORS" ボタン。
 *
 * Colors セクション末尾に置き、クリックで Surface / Inner / Text の
 * 調和トリオを OKLCH 空間で再生成する（Background は対象外）。
 *
 * 旧仕様（Phase B）は「Text 色は触らない」だったが、2026-07-10 のユーザ
 * 指示でトリオ生成（Text = 面に乗る低彩度インク）へ拡張された。役割定義と
 * アンカー導出は `core/color.randomHarmoniousTrio` の JSDoc を参照。
 *
 * ロックとの関係:
 * - ロック色は OKLCH アンカーとして残りの色の導出基準になる
 * - ロック色自体は書き込まない（鍵が縛るのはランダマイズの上書きのみ）
 * - 3 色すべてロック時はボタンを disabled（arare-panel 作法: dim +
 *   cursor: not-allowed。app.css の .btn:disabled が両方を与える）
 *
 * 見た目は `mountShuffle`（Fragmentation の `↻ Shuffle Seed`）と同系統の
 * `btn ghost` を踏襲。`↻` は Unicode 幾何記号（U+21BB）で絵文字ではない
 * ため、docs/happa-designtool.md §2.1「絵文字禁止」原則に抵触しない。
 */

import { randomHarmoniousTrio, type TrioAnchors } from '../../core/color';
import type { AppState, Store } from '../../state';

/**
 * Randomize Colors ボタンをマウントする。
 *
 * @param root - Colors セクションのコンテナ
 * @param store - グローバルストア
 * @returns teardown 関数
 */
export function mountRandomizeColors(root: HTMLElement, store: Store<AppState>): () => void {
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = 'btn ghost btn-randomize-colors';
  btn.textContent = '↻ RANDOMIZE COLORS';

  const onClick = (): void => {
    const state = store.get();
    const locks = state.colorLocks;

    // 全ロック時はボタン自体が disabled（syncDisabled）だが、防御的に no-op。
    if (locks.surfaceColor && locks.innerColor && locks.textColor) return;

    // ロック色をアンカーに持ち上げる。exactOptionalPropertyTypes のため
    // undefined の明示代入はせず、ロック時のみキーを立てる。
    const anchors: TrioAnchors = {};
    if (locks.surfaceColor) anchors.surface = state.surfaceColor;
    if (locks.innerColor) anchors.inner = state.innerColor;
    if (locks.textColor) anchors.text = state.textColor;

    // Text の導出は現在の色相モード（Text Harmony セグメント）に従う。
    const trio = randomHarmoniousTrio(anchors, state.textMode);

    // ロックされていない色だけを書き込む（アンカーは素通しだが念のため除外）。
    const patch: Partial<AppState> = {};
    if (!locks.surfaceColor) patch.surfaceColor = trio.surface;
    if (!locks.innerColor) patch.innerColor = trio.inner;
    if (!locks.textColor) patch.textColor = trio.text;
    store.set(patch);
  };
  btn.addEventListener('click', onClick);

  // ランダマイズ対象（Surface / Inner / Text）が全ロックなら disabled。
  // Background はランダマイズ対象外なので disable 判定に関与しない。
  const syncDisabled = (): void => {
    const locks = store.get().colorLocks;
    btn.disabled = locks.surfaceColor && locks.innerColor && locks.textColor;
  };
  syncDisabled();

  const unsubscribe = store.subscribe((state, prev) => {
    if (state.colorLocks !== prev.colorLocks) syncDisabled();
  });

  root.appendChild(btn);

  return (): void => {
    unsubscribe();
    btn.removeEventListener('click', onClick);
    btn.remove();
  };
}
